import { useMemo, useState } from 'react';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import type { JobFileEntry } from '@jobos/shared';

import { JobFileDragPreview } from './JobFileDragPreview';
import { resolveDrop, type DropResolution } from './jobFileDrag';

// The drag context for the job file area: the sensors, the thing that follows
// the pointer, and the routing of a finished drag into a move.
//
// MouseSensor and TouchSensor rather than one PointerSensor, because the two
// devices want different rules about when a drag has begun and a single pointer
// sensor can only hold one of them.

interface JobFileDndProviderProps {
  jobId: string;
  /** The files currently on screen — the only ones that can be dragged. */
  files: JobFileEntry[];
  selectedIds: ReadonlySet<string>;
  onSelectionChange: (ids: string[]) => void;
  onDrop: (resolution: DropResolution) => void;
  children: React.ReactNode;
}

export function JobFileDndProvider({
  jobId,
  files,
  selectedIds,
  onSelectionChange,
  onDrop,
  children,
}: JobFileDndProviderProps) {
  const [dragging, setDragging] = useState<{
    file: JobFileEntry;
    count: number;
    origin: { x: number; y: number };
  } | null>(null);

  const sensors = useSensors(
    // 8px of travel before a mouse drag begins, so a plain click still reaches
    // the card's own onClick and opens the file.
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    // A press, not a tap. Under 250ms the touch is still a tap and opens the
    // file, which is what a phone user does far more often than they file.
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const fileFolderIds = useMemo(
    () => new Map(files.map((file) => [file.id, file.folderId])),
    [files],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    const file = files.find((candidate) => candidate.id === id);
    if (!file) return;

    // Picking up a file from outside the selection is a gesture about that one
    // file, so the old selection goes rather than quietly coming along.
    const inSelection = selectedIds.has(id);
    if (!inSelection && selectedIds.size > 0) onSelectionChange([]);

    // Where the press landed, so the preview has a position for the frame
    // before the first pointermove arrives.
    const origin = getEventCoordinates(event.activatorEvent) ?? { x: 0, y: 0 };

    setDragging({ file, count: inSelection ? selectedIds.size : 1, origin });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDragging(null);
    const resolution = resolveDrop({
      activeId: String(event.active.id),
      overId: event.over ? String(event.over.id) : null,
      selectedIds,
      fileFolderIds,
    });
    if (resolution) onDrop(resolution);
  };

  return (
    <DndContext
      sensors={sensors}
      // What the cursor is over, not what the preview's rectangle overlaps. The
      // preview is a small stand-in for a whole card, so rect intersection —
      // the default — would pick folders by where that square happens to land
      // rather than by where the user is pointing.
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDragging(null)}
    >
      {children}

      {dragging && (
        <JobFileDragPreview
          jobId={jobId}
          file={dragging.file}
          count={dragging.count}
          origin={dragging.origin}
        />
      )}
    </DndContext>
  );
}
